import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { TPreIntake } from '../../../../types/intake';
import useLocationHook from '../../../../hooks/modules/mdm/useLocationHook';
import { TLocation } from '../../../../types/mdm';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import useCookies from '../../../../hooks/useCookiesHook';
import useDistrictAccessHook from 'hooks/modules/useDistrictAccessHook';

interface PreIntakeLocationFormProps {
  preIntake?: TPreIntake;
}

const PreIntakeLocationForm = ({ preIntake }: PreIntakeLocationFormProps) => {
  // eslint-disable-next-line
  const { t }: { t: any } = useTranslation();
  const { isEnglishLanguage } = useCookies();
  const { values, setFieldValue, handleBlur, errors, touched } =
    useFormikContext<TPreIntake>();
  const { fetchAllLocation, locations } = useLocationHook();
  const { mode, assignedDistricts, assignedDistrictIds, assignedProvinces } =
    useDistrictAccessHook();

  // Single-district users get a province/district locked to their one district.
  const isDistrictLocked = mode === 'single';
  const scopedDistrict = isDistrictLocked ? assignedDistricts[0] : undefined;
  const assignedProvinceIds = assignedProvinces.map(p => p.id);
  useEffect(() => {
    // fetchAllLocation().catch(e => console.log(e));
    if (locations.length <= 0) {
      fetchAllLocation().catch(e => console.log(e));
    }
  }, []);
  const [locationState, setLocationState] = useState({
    provinces: [] as TLocation[],
    districts: [] as TLocation[],
    communes: [] as TLocation[],
    villages: [] as TLocation[]
  });
  useEffect(() => {
    setLocationState(prev => ({
      ...prev,
      provinces: locations.filter(data => !data.parent_id)
    }));
  }, [locations]);
  useEffect(() => {
    setLocationState(prev => ({
      ...prev,
      districts:
        locations.filter(data => data.parent_id == preIntake?.province) || []
    }));
  }, [preIntake?.province]);

  useEffect(() => {
    setLocationState(prev => ({
      ...prev,
      communes:
        locations.filter(data => data.parent_id == preIntake?.district) || []
    }));
  }, [preIntake?.district]);
  useEffect(() => {
    setLocationState(prev => ({
      ...prev,
      villages:
        locations.filter(data => data.parent_id == preIntake?.commune) || []
    }));
  }, [preIntake?.commune]);
  const updateLocation = (
    id: string,
    parentLevel: keyof typeof locationState,
    childLevel: keyof typeof locationState
  ) => {
    const children = id
      ? locations?.filter(data => data.parent_id === parseInt(id))
      : [];

    setLocationState(prevState => ({
      ...prevState,
      [childLevel]: children,
      ...(childLevel === 'provinces' && {
        districts: [],
        communes: [],
        villages: []
      }),
      ...(childLevel === 'districts' && { communes: [], villages: [] }),
      ...(childLevel === 'communes' && { villages: [] })
    }));
  };

  // Pre-fill and lock province/district for single-district users (Spec D-1/D-4).
  useEffect(() => {
    if (
      isDistrictLocked &&
      scopedDistrict &&
      !values.province &&
      !values.district
    ) {
      setFieldValue('province', scopedDistrict.parent_id);
      setFieldValue('district', scopedDistrict.id);
      updateLocation(String(scopedDistrict.id ?? ''), 'districts', 'communes');
    }
  }, [isDistrictLocked, scopedDistrict, locations]);

  // Province/district options constrained to the user's scope (Spec D-2/D-3).
  const provinceOptions =
    mode === 'full'
      ? locationState.provinces
      : locationState.provinces.filter(p => assignedProvinceIds.includes(p.id));

  const districtOptions =
    mode === 'full'
      ? locationState.districts
      : mode === 'single'
        ? assignedDistricts
        : locationState.districts.filter(d =>
            assignedDistrictIds.includes(d.id as number)
          );

  return (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-3" controlId="formGroupProvince">
          <Form.Label>
            {t('province')}
            <span className="text-danger ms-1">*</span>
          </Form.Label>
          <Form.Select
            value={values?.province || ''}
            name="province"
            onBlur={handleBlur}
            disabled={isDistrictLocked}
            onChange={e => {
              const selectedId = e.target.value;
              setFieldValue('province', selectedId);
              updateLocation(selectedId, 'provinces', 'districts');
            }}
            className={`${
              touched.province && errors.province ? 'is-invalid' : ''
            }`}
          >
            <option value="">{t('select_province')}</option>

            {provinceOptions.map((data, i) => (
              <option key={`province-${i}`} value={data.id as number}>
                {`${!isEnglishLanguage ? data.name : data.name_alt || data.name} - ${data.code}`}
              </option>
            ))}
          </Form.Select>
          {touched.province && errors.province && (
            <Form.Control.Feedback type="invalid">
              {errors.province}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group className="mb-3" controlId="formGroupDistrict">
          <Form.Label>
            {t('district')}
            <span className="text-danger ms-1">*</span>
          </Form.Label>
          <Form.Select
            value={values?.district || ''}
            name="district"
            onBlur={handleBlur}
            disabled={isDistrictLocked}
            onChange={e => {
              const selectedId = e.target.value;
              setFieldValue('district', selectedId);
              updateLocation(selectedId, 'districts', 'communes');
            }}
            className={`${
              touched.district && errors.district ? 'is-invalid' : ''
            }`}
          >
            <option value="">{t('select_district')}</option>
            {districtOptions.map((data, i) => (
              <option key={`district-${i}`} value={data.id as number}>
                {`${!isEnglishLanguage ? data.name : data.name_alt || data.name} - ${data.code}`}
              </option>
            ))}
          </Form.Select>
          {touched.district && errors.district && (
            <Form.Control.Feedback type="invalid">
              {errors.district}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group className="mb-3" controlId="formGroupCommune">
          <Form.Label>{t('commune_optional')}</Form.Label>
          <Form.Select
            value={values?.commune || ''}
            name="commune"
            onBlur={handleBlur}
            onChange={e => {
              const selectedId = e.target.value;
              setFieldValue('commune', selectedId);
              updateLocation(selectedId, 'communes', 'villages');
            }}
          >
            <option value="">{t('select_commune')}</option>
            {locationState.communes.map((data, i) => (
              <option key={`commune-${i}`} value={data.id as number}>
                {`${!isEnglishLanguage ? data.name : data.name_alt || data.name} - ${data.code}`}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group className="mb-3" controlId="formGroupVillage">
          <Form.Label>{t('village_optional')}</Form.Label>
          <Form.Select
            value={values?.village || ''}
            name="village"
            onBlur={handleBlur}
            onChange={e => {
              const selectedId = e.target.value;
              setFieldValue('village', selectedId);
            }}
          >
            <option value="">{t('select_village')}</option>
            {locationState.villages.map((data, i) => (
              <option key={`village-${i}`} value={data.id as number}>
                {`${!isEnglishLanguage ? data.name : data.name_alt || data.name} - ${data.code}`}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
    </Row>
  );
};

export default PreIntakeLocationForm;
